import React, { useEffect, useMemo, useState } from 'react'
import i18next from 'i18next'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import OracleComingSoon from './OracleComingSoon'
import PolymarketOrderBook from './PolymarketOrderBook'
import TelegramPromoCta from '../common/TelegramPromoCta'
import {
  formatLastUpdate,
  getLeagueLabel,
  getMinute,
  getParticipants,
  getRisk,
  getScore,
  getTeamIndexValue,
  getTeamStatProfile,
  getTriggerLabel,
} from '../../features/live-market/liveMarketUtils'

const tr = (value, fallback = value) => i18next.t(value, { defaultValue: fallback })

const INDEX_SERIES = [
  { id: 'pi', label: 'Pressure Index', short: 'PI', color: '#19f05a' },
  { id: 'ci', label: 'Control Index', short: 'CI', color: '#f2f4f7' },
  { id: 'vi', label: 'Volatility Index', short: 'VI', color: '#ffd247' },
  { id: 'ri', label: 'Resistance Index', short: 'RI', color: '#ff4b3e' },
]

const CATEGORY_DEFS = [
  {
    id: 'offensive',
    label: 'Offensive Pressure',
    stats: [
      'Goals',
      'Shots Total',
      'Shots On Target',
      'Shots Off Target',
      'Shots Blocked',
      'Shots Insidebox',
      'Shots Outsidebox',
      'Big Chances Created',
      'Big Chances Missed',
      'Corners',
      'Attacks',
      'Dangerous Attacks',
      'Total Crosses',
      'Accurate Crosses',
      'Key Passes',
    ],
  },
  {
    id: 'territorial',
    label: 'Territorial Control',
    stats: [
      'Ball Possession %',
      'Passes',
      'Successful Passes',
      'Successful Passes Percentage',
      'Long Passes',
      'Successful Long Passes',
      'Successful Long Passes Percentage',
      'Free Kicks',
      'Throwins',
      'Goal Kicks',
    ],
  },
  {
    id: 'duel',
    label: 'Duel & Intensity',
    stats: [
      'Duels Won',
      'Dribble Attempts',
      'Successful Dribbles',
      'Successful Dribbles Percentage',
      'Tackles',
      'Fouls',
      'Offsides',
    ],
  },
  {
    id: 'defensive',
    label: 'Defensive Resistance',
    stats: ['Saves', 'Interceptions'],
  },
  {
    id: 'state',
    label: 'Game State',
    stats: ['Yellowcards'],
  },
]

const RADAR_STAT_DEFS = [
  { label: 'Goals', kind: 'goal' },
  { label: 'Shots Total', kind: 'shot-target' },
  { label: 'Shots On Target', kind: 'shot-target' },
  { label: 'Shots Off Target', kind: 'shot-off-target' },
  { label: 'Shots Blocked', kind: 'blocked-shot' },
  { label: 'Corners', kind: 'corner' },
  { label: 'Attacks', kind: 'attack' },
  { label: 'Dangerous Attacks', kind: 'dangerous-attack' },
  { label: 'Ball Possession %', kind: 'control' },
  { label: 'Fouls', kind: 'foul' },
  { label: 'Tackles', kind: 'tackle' },
  { label: 'Interceptions', kind: 'interception' },
  { label: 'Saves', kind: 'save' },
  { label: 'Yellowcards', kind: 'yellow-card' },
]

const STAT_ALIASES = {
  'Accurate Crosses': ['accurate crosses'],
  Attacks: ['attacks'],
  'Ball Possession %': ['ball possession %', 'ball possession', 'possession'],
  'Big Chances Created': ['big chances created'],
  'Big Chances Missed': ['big chances missed'],
  Corners: ['corners', 'corner kicks'],
  'Dangerous Attacks': ['dangerous attacks'],
  'Dribble Attempts': ['dribble attempts'],
  'Duels Won': ['duels won'],
  Fouls: ['fouls'],
  'Free Kicks': ['free kicks'],
  'Goal Kicks': ['goal kicks'],
  Goals: ['goals'],
  Interceptions: ['interceptions'],
  'Key Passes': ['key passes'],
  'Long Passes': ['long passes', 'long balls'],
  Offsides: ['offsides'],
  Passes: ['passes', 'passes total'],
  Saves: ['saves'],
  'Red Cards': ['red cards', 'redcards'],
  'Shots Blocked': ['shots blocked', 'blocked shots'],
  'Shots Insidebox': ['shots insidebox', 'shots inside box'],
  'Shots Off Target': ['shots off target', 'shots off goal'],
  'Shots On Target': ['shots on target', 'shots on goal'],
  'Shots Outsidebox': ['shots outsidebox', 'shots outside box'],
  'Shots Total': ['shots total', 'total shots'],
  'Successful Dribbles': ['successful dribbles'],
  'Successful Dribbles Percentage': ['successful dribbles percentage'],
  'Successful Long Passes': ['successful long passes'],
  'Successful Long Passes Percentage': ['successful long passes percentage'],
  'Successful Passes': ['successful passes', 'passes accurate', 'accurate passes'],
  'Successful Passes Percentage': ['successful passes percentage', 'passes accuracy', 'pass accuracy'],
  Tackles: ['tackles', 'tackles won'],
  Throwins: ['throwins', 'throw-ins', 'throw ins'],
  'Total Crosses': ['total crosses', 'crosses'],
  Yellowcards: ['yellowcards', 'yellow cards'],
}

const TRACKED_TAPE_STATS = [
  { stat: 'Goals', historyKey: 'goals', title: 'Goal', tone: 'pressure', icon: 'bi-dribbble' },
  { stat: 'Shots On Target', historyKey: 'shotsOnTarget', title: 'Shot on target', tone: 'pressure', icon: 'bi-bullseye' },
  { stat: 'Shots Off Target', historyKey: 'shotsOffTarget', title: 'Shot off target', tone: 'neutral', icon: 'bi-arrow-up-right-circle' },
  { stat: 'Shots Blocked', historyKey: 'shotsBlocked', title: 'Blocked shot', tone: 'neutral', icon: 'bi-shield' },
  { stat: 'Shots Insidebox', historyKey: 'shotsInsideBox', title: 'Shot inside the box', tone: 'pressure', icon: 'bi-bounding-box' },
  { stat: 'Shots Outsidebox', historyKey: 'shotsOutsideBox', title: 'Shot outside the box', tone: 'neutral', icon: 'bi-bounding-box-circles' },
  { stat: 'Big Chances Created', historyKey: 'bigChancesCreated', title: 'Big chance created', tone: 'pressure', icon: 'bi-lightning-charge-fill' },
  { stat: 'Big Chances Missed', historyKey: 'bigChancesMissed', title: 'Big chance missed', tone: 'risk', icon: 'bi-exclamation-diamond' },
  { stat: 'Corners', historyKey: 'corners', title: 'Corner', tone: 'pressure', icon: 'bi-flag' },
  { stat: 'Attacks', historyKey: 'attacks', title: 'Attack', tone: 'neutral', icon: 'bi-arrow-right' },
  { stat: 'Dangerous Attacks', historyKey: 'dangerousAttacks', title: 'Dangerous attack', tone: 'pressure', icon: 'bi-bar-chart-line' },
  { stat: 'Key Passes', historyKey: 'keyPasses', title: 'Key pass', tone: 'pressure', icon: 'bi-signpost-split' },
  { stat: 'Fouls', historyKey: 'fouls', title: 'Foul', tone: 'risk', icon: 'bi-slash-circle' },
  { stat: 'Tackles', historyKey: 'tackles', title: 'Tackle', tone: 'neutral', icon: 'bi-shield-check' },
  { stat: 'Offsides', historyKey: 'offsides', title: 'Offside', tone: 'risk', icon: 'bi-distribute-horizontal' },
  { stat: 'Saves', historyKey: 'saves', title: 'Save', tone: 'neutral', icon: 'bi-hand-index-thumb' },
  { stat: 'Interceptions', historyKey: 'interceptions', title: 'Interception', tone: 'neutral', icon: 'bi-intersect' },
  { stat: 'Free Kicks', historyKey: 'freeKicks', title: 'Free kick', tone: 'neutral', icon: 'bi-circle', radarOnly: true },
  { stat: 'Throwins', historyKey: 'throwins', title: 'Throw-in', tone: 'neutral', icon: 'bi-arrow-up', radarOnly: true },
  { stat: 'Goal Kicks', historyKey: 'goalKicks', title: 'Goal kick', tone: 'neutral', icon: 'bi-box-arrow-up-right', radarOnly: true },
  { stat: 'Duels Won', historyKey: 'duelsWon', title: 'Duel won', tone: 'neutral', icon: 'bi-arrows-angle-contract', radarOnly: true },
  { stat: 'Dribble Attempts', historyKey: 'dribbleAttempts', title: 'Dribble attempt', tone: 'neutral', icon: 'bi-bezier2', radarOnly: true },
  { stat: 'Successful Dribbles', historyKey: 'successfulDribbles', title: 'Successful dribble', tone: 'pressure', icon: 'bi-bezier2', radarOnly: true },
  { stat: 'Total Crosses', historyKey: 'crosses', title: 'Cross', tone: 'neutral', icon: 'bi-signpost-2', radarOnly: true },
  { stat: 'Accurate Crosses', historyKey: 'accurateCrosses', title: 'Accurate cross', tone: 'pressure', icon: 'bi-signpost-2-fill', radarOnly: true },
  { stat: 'Yellowcards', historyKey: 'yellowCards', title: 'Yellow card', tone: 'risk', icon: 'bi-square-fill' },
  { stat: 'Red Cards', historyKey: 'redCards', title: 'Red card', tone: 'risk', icon: 'bi-square-fill' },
]

const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, Number(value) || 0))

const useCompactViewport = (maxWidth = 900) => {
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches : false
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`)
    const handleChange = () => setIsCompact(mediaQuery.matches)
    handleChange()
    mediaQuery.addEventListener?.('change', handleChange)
    return () => mediaQuery.removeEventListener?.('change', handleChange)
  }, [maxWidth])

  return isCompact
}

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const getStatAliasLabel = (statName) => {
  const aliases = Object.entries(STAT_ALIASES)
  const exactMatch = aliases.find(([, candidates]) =>
    candidates.some((candidate) => statName === normalize(candidate))
  )
  if (exactMatch) return exactMatch[0]

  return aliases
    .flatMap(([label, candidates]) => candidates.map((candidate) => ({ label, alias: normalize(candidate) })))
    .sort((left, right) => right.alias.length - left.alias.length)
    .find(({ alias }) => statName.includes(alias))?.label
}

const TAPE_EVENT_COPY_PT = [
  { terms: ['disallowed goal'], title: 'Golo anulado', description: 'Foi registado um golo anulado.' },
  { terms: ['goal'], title: 'Golo', description: 'Foi registado um golo para esta equipa.' },
  { terms: ['shot on target'], title: 'Remate à baliza', description: 'Foi registado um remate enquadrado com a baliza.' },
  { terms: ['shot off target'], title: 'Remate para fora', description: 'Foi registado um remate que não acertou na baliza.' },
  { terms: ['blocked shot'], title: 'Remate bloqueado', description: 'Foi registado um remate bloqueado pela defesa.' },
  { terms: ['shot inside the box'], title: 'Remate dentro da área', description: 'Foi registado um remate dentro da área.' },
  { terms: ['shot outside the box'], title: 'Remate fora da área', description: 'Foi registado um remate fora da área.' },
  { terms: ['big chance created'], title: 'Grande oportunidade criada', description: 'Foi criada uma grande oportunidade de golo.' },
  { terms: ['big chance missed'], title: 'Grande oportunidade perdida', description: 'Foi desperdiçada uma grande oportunidade de golo.' },
  { terms: ['corner'], title: 'Canto', description: 'Foi conquistado um canto para esta equipa.' },
  { terms: ['dangerous attack'], title: 'Ataque perigoso', description: 'Foi registado um ataque perigoso desta equipa.' },
  { terms: ['attack'], title: 'Ataque', description: 'Foi registado um novo ataque desta equipa.' },
  { terms: ['key pass'], title: 'Passe-chave', description: 'Foi registado um passe-chave que criou perigo.' },
  { terms: ['foul'], title: 'Falta', description: 'Foi registada uma falta cometida por esta equipa.' },
  { terms: ['tackle'], title: 'Desarme', description: 'Foi registado um desarme efetuado por esta equipa.' },
  { terms: ['offside'], title: 'Fora de jogo', description: 'Foi assinalado um fora de jogo a esta equipa.' },
  { terms: ['save'], title: 'Defesa', description: 'Foi registada uma defesa do guarda-redes.' },
  { terms: ['interception'], title: 'Interceção', description: 'Foi registada uma interceção efetuada por esta equipa.' },
  { terms: ['free kick'], title: 'Livre', description: 'Foi assinalado um livre a favor desta equipa.' },
  { terms: ['throw-in'], title: 'Lançamento lateral', description: 'Foi registado um lançamento lateral desta equipa.' },
  { terms: ['goal kick'], title: 'Pontapé de baliza', description: 'Foi registado um pontapé de baliza desta equipa.' },
  { terms: ['duel won'], title: 'Duelo ganho', description: 'Foi registado um duelo ganho por esta equipa.' },
  { terms: ['successful dribble'], title: 'Drible conseguido', description: 'Foi registado um drible bem-sucedido desta equipa.' },
  { terms: ['dribble attempt'], title: 'Tentativa de drible', description: 'Foi registada uma tentativa de drible desta equipa.' },
  { terms: ['accurate cross'], title: 'Cruzamento eficaz', description: 'Foi registado um cruzamento eficaz desta equipa.' },
  { terms: ['cross'], title: 'Cruzamento', description: 'Foi registado um cruzamento desta equipa.' },
  { terms: ['yellow card', 'yellowcard'], title: 'Cartão amarelo', description: 'Foi mostrado um cartão amarelo a esta equipa.' },
  { terms: ['red card', 'redcard'], title: 'Cartão vermelho', description: 'Foi mostrado um cartão vermelho a esta equipa.' },
  { terms: ['substitution'], title: 'Substituição', description: 'Foi registada uma substituição nesta equipa.' },
]

const getTapeEventCopy = (event) => {
  const normalizedTitle = normalize(event?.title)
  const copy = TAPE_EVENT_COPY_PT.find((item) => item.terms.some((term) => normalizedTitle.includes(term)))
  if (copy) return copy
  return {
    title: event?.title || 'Evento live',
    description: event?.source === 'stat-delta'
      ? 'Foi registada uma alteração numa estatística live desta equipa.'
      : 'Evento oficial recebido do fornecedor de dados.',
  }
}

const getTapeEventVisual = (event) => {
  const normalizedTitle = normalize(event?.title)
  if (normalizedTitle.includes('disallowed goal')) return { kind: 'disallowed-goal' }
  if (normalizedTitle.includes('goal') && !normalizedTitle.includes('kick')) return { kind: 'goal' }
  if (normalizedTitle.includes('yellow')) return { kind: 'yellow-card' }
  if (normalizedTitle.includes('red')) return { kind: 'red-card' }
  if (normalizedTitle.includes('shot on target')) return { kind: 'shot-target' }
  if (normalizedTitle.includes('shot off target')) return { kind: 'shot-off-target' }
  if (normalizedTitle.includes('blocked shot')) return { kind: 'blocked-shot' }
  if (normalizedTitle.includes('inside the box')) return { kind: 'inside-box' }
  if (normalizedTitle.includes('outside the box')) return { kind: 'outside-box' }
  if (normalizedTitle.includes('big chance missed')) return { kind: 'big-chance-missed' }
  if (normalizedTitle.includes('big chance')) return { kind: 'big-chance' }
  if (normalizedTitle.includes('corner')) return { kind: 'corner' }
  if (normalizedTitle.includes('dangerous attack')) return { kind: 'dangerous-attack' }
  if (normalizedTitle.includes('attack')) return { kind: 'attack' }
  if (normalizedTitle.includes('key pass')) return { kind: 'key-pass' }
  if (normalizedTitle.includes('foul')) return { kind: 'foul' }
  if (normalizedTitle.includes('tackle')) return { kind: 'tackle' }
  if (normalizedTitle.includes('offside')) return { kind: 'offside' }
  if (normalizedTitle.includes('save')) return { kind: 'save' }
  if (normalizedTitle.includes('interception')) return { kind: 'interception' }
  if (normalizedTitle.includes('substitution')) return { kind: 'substitution' }
  if (normalizedTitle.includes('free kick')) return { kind: 'free-kick' }
  if (normalizedTitle.includes('throw-in')) return { kind: 'throw-in' }
  if (normalizedTitle.includes('goal kick')) return { kind: 'goal-kick' }
  if (normalizedTitle.includes('duel')) return { kind: 'duel' }
  if (normalizedTitle.includes('dribble')) return { kind: 'dribble' }
  if (normalizedTitle.includes('cross')) return { kind: 'cross' }
  if (event?.tone === 'risk') return { kind: 'risk' }
  if (event?.tone === 'pressure') return { kind: 'pressure' }
  return { kind: 'activity' }
}

const TapeEventIcon = ({ kind }) => {
  if (kind === 'goal') return <span className="event-icon-ball">⚽</span>
  if (kind === 'yellow-card' || kind === 'red-card') return <span className="event-icon-card" />

  const commonProps = {
    'aria-hidden': true,
    fill: 'none',
    viewBox: '0 0 24 24',
  }

  switch (kind) {
    case 'attack':
      return <svg {...commonProps}><path d="M3 12h16m-6-6 6 6-6 6" /></svg>
    case 'dangerous-attack':
      return <svg {...commonProps}><path d="M2 12h13m-7-6 6 6-6 6m5-12 6 6-6 6" /></svg>
    case 'shot-target':
      return <svg {...commonProps}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 1v3m0 16v3M1 12h3m16 0h3" /></svg>
    case 'shot-off-target':
      return <svg {...commonProps}><circle cx="8" cy="15" r="5" /><path d="m11 11 9-7m-5 0h5v5" /></svg>
    case 'blocked-shot':
      return <svg {...commonProps}><path d="M12 2 4 5v6c0 5 3.3 8.6 8 11 4.7-2.4 8-6 8-11V5l-8-3Z" /><path d="m8 9 8 6m0-6-8 6" /></svg>
    case 'inside-box':
      return <svg {...commonProps}><rect x="3" y="5" width="18" height="14" rx="1" /><circle cx="12" cy="12" r="2.5" /></svg>
    case 'outside-box':
      return <svg {...commonProps}><rect x="6" y="6" width="13" height="12" rx="1" /><circle cx="3" cy="12" r="2" /><path d="M5 12h5" /></svg>
    case 'big-chance':
      return <svg {...commonProps}><path d="m13 2-8 12h6l-1 8 9-13h-6V2Z" /></svg>
    case 'big-chance-missed':
      return <svg {...commonProps}><path d="m12 2 10 10-10 10L2 12 12 2Z" /><path d="M12 7v7m0 3h.01" /></svg>
    case 'corner':
      return <svg {...commonProps}><path d="M5 22V3m0 1h11l-3 4 3 4H5M2 22h7" /></svg>
    case 'key-pass':
      return <svg {...commonProps}><path d="M3 12h8m0 0 6-6m-6 6 6 6m0-12h4m-4 12h4" /></svg>
    case 'foul':
    case 'risk':
      return <svg {...commonProps}><circle cx="12" cy="12" r="9" /><path d="M12 6v8m0 3h.01" /></svg>
    case 'tackle':
      return <svg {...commonProps}><path d="M12 2 4 5v6c0 5 3.3 8.6 8 11 4.7-2.4 8-6 8-11V5l-8-3Z" /><path d="m8 12 2.5 2.5L16 9" /></svg>
    case 'offside':
      return <svg {...commonProps}><path d="M3 5v14m5-14v14m5-14v14m5-14v14" /><circle cx="18" cy="8" r="2" /><path d="M18 10v7m-3-3h6" /></svg>
    case 'save':
      return <svg {...commonProps}><circle cx="12" cy="12" r="3" /><path d="M3 9V5h4M3 15v4h4m14-10V5h-4m4 10v4h-4" /></svg>
    case 'interception':
      return <svg {...commonProps}><path d="M4 5c5 0 6 14 11 14h5m-4-4 4 4-4 4M4 19c5 0 6-14 11-14h5m-4-4 4 4-4 4" /></svg>
    case 'substitution':
      return <svg {...commonProps}><path d="M3 8h15m-4-4 4 4-4 4M21 16H6m4-4-4 4 4 4" /></svg>
    case 'free-kick':
      return <svg {...commonProps}><circle cx="7" cy="16" r="3" /><path d="M11 15c4-1 6-4 8-9m-4 1 4-1 1 4" /></svg>
    case 'throw-in':
      return <svg {...commonProps}><circle cx="12" cy="5" r="2" /><path d="M8 12c1-3 2-4 4-4s3 1 4 4m-8 0-3-2m11 2 3-2M12 8v13" /></svg>
    case 'goal-kick':
      return <svg {...commonProps}><path d="M3 20V7h10v13M3 12h10" /><circle cx="17" cy="17" r="2" /><path d="m16 14 4-8m-3 2 3-2 1 3" /></svg>
    case 'duel':
      return <svg {...commonProps}><path d="m4 5 16 14m0-14L4 19M4 5v5m0-5h5m11 0v5m0-5h-5" /></svg>
    case 'dribble':
      return <svg {...commonProps}><circle cx="5" cy="18" r="2" /><circle cx="19" cy="6" r="2" /><path d="M7 18c7 0 1-8 7-8h3" /></svg>
    case 'cross':
      return <svg {...commonProps}><path d="M3 17c5-7 10-9 18-9m-5-4 5 4-4 4" /><circle cx="5" cy="17" r="2" /></svg>
    case 'disallowed-goal':
      return <svg {...commonProps}><circle cx="12" cy="12" r="7" /><path d="M5 5l14 14" /></svg>
    case 'control':
      return <svg {...commonProps}><path d="M4 18V9m5 9V5m5 13v-7m5 7V3" /></svg>
    default:
      return <svg {...commonProps}><path d="M2 13h4l2-6 4 11 3-7 2 2h5" /></svg>
  }
}

const getLegacyTapeEventSymbol = (event, visual) => {
  if (visual.kind === 'goal') return '⚽'
  if (visual.kind === 'yellow-card' || visual.kind === 'red-card') return '■'
  if (event?.tone === 'risk') return '!'
  if (event?.tone === 'pressure') return '●'
  return '•'
}

const readStatValue = (stat) => {
  const raw = stat?.data?.value ?? stat?.value ?? 0
  if (typeof raw === 'string') {
    const parsed = Number(raw.replace('%', '').replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : 0
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

const getCurrentMinuteNumber = (match) => {
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const periodMinute = periods.reduce((highest, period) => Math.max(highest, Number(period?.minutes) || 0), 0)
  if (periodMinute > 0) return periodMinute
  const timeMinute = Number(match?.time?.minute || match?.currentPeriod?.minutes || match?.current_period?.minutes || 0)
  if (timeMinute > 0) return timeMinute
  const minuteText = String(getMinute(match) || '').match(/\d+/)
  return minuteText ? Number(minuteText[0]) : 0
}

const getChartEndMinute = (match, history) => {
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const periodEnd = periods.reduce((highest, period) => {
    const minutes = Number(period?.minutes) || 0
    const added = Number(period?.time_added) || 0
    return Math.max(highest, minutes + added)
  }, 0)
  const eventEnd = (match?.events || []).reduce((highest, event) => {
    const minute = Number(event?.minute) || 0
    const extra = Number(event?.extra_minute) || 0
    return Math.max(highest, minute + extra)
  }, 0)
  const historyEnd = (history || []).reduce((highest, point) => Math.max(highest, Number(point?.minute) || 0), 0)
  return Math.max(90, periodEnd, eventEnd, historyEnd)
}

const getChartTicks = (endMinute) => {
  const baseTicks = [0, 15, 30, 45, 60, 75, 90]
  if (endMinute <= 90) return baseTicks
  const extraTicks = []
  for (let minute = 95; minute <= endMinute; minute += 5) {
    extraTicks.push(minute)
  }
  return [...baseTicks, ...extraTicks]
}

const getTeamSide = (match, participantId) => {
  const { home, away } = getParticipants(match)
  if (String(participantId) === String(home?.id)) return 'home'
  if (String(participantId) === String(away?.id)) return 'away'
  return null
}

const makeEmptyStats = () =>
  Object.keys(STAT_ALIASES).reduce((accumulator, label) => {
    accumulator[label] = { home: 0, away: 0 }
    return accumulator
  }, {})

const applyFallbackStats = (match, stats) => {
  const indexes = match?.liveIndexes || {}
  const score = getScore(match)
  const pressure = indexes.pressureIndex || {}
  const control = indexes.controlIndex || {}
  const resistance = indexes.resistanceIndex || {}

  stats.Goals.home = Number(score.home) || 0
  stats.Goals.away = Number(score.away) || 0

  if (Object.values(stats).some((pair) => pair.home || pair.away)) return stats

  const homePressure = Number(pressure.home) || 50
  const awayPressure = Number(pressure.away) || 50
  const homeControl = Number(control.home) || 50
  const awayControl = Number(control.away) || 50
  const homeResistance = Number(resistance.home) || 50
  const awayResistance = Number(resistance.away) || 50

  stats['Shots Total'] = { home: Math.round(homePressure / 8), away: Math.round(awayPressure / 8) }
  stats['Shots On Target'] = { home: Math.round(homePressure / 22), away: Math.round(awayPressure / 22) }
  stats.Attacks = { home: Math.round(homePressure * 1.2), away: Math.round(awayPressure * 1.2) }
  stats['Dangerous Attacks'] = { home: Math.round(homePressure / 3), away: Math.round(awayPressure / 3) }
  stats.Corners = { home: Math.round(homePressure / 22), away: Math.round(awayPressure / 22) }
  stats['Ball Possession %'] = { home: Math.round(homeControl), away: Math.round(awayControl) }
  stats.Passes = { home: Math.round(homeControl * 4.6), away: Math.round(awayControl * 4.6) }
  stats['Successful Passes Percentage'] = {
    home: Math.round(68 + homeControl / 5),
    away: Math.round(68 + awayControl / 5),
  }
  stats.Tackles = { home: Math.round(homeResistance / 4), away: Math.round(awayResistance / 4) }
  stats.Interceptions = { home: Math.round(homeResistance / 5), away: Math.round(awayResistance / 5) }
  stats.Saves = { home: Math.round(awayPressure / 28), away: Math.round(homePressure / 28) }
  return stats
}

const collectOrderBookStats = (match) => {
  const stats = makeEmptyStats()
  const providerStats = Array.isArray(match?.statistics) ? match.statistics : []

  providerStats.forEach((stat) => {
    const side = getTeamSide(match, stat?.participant_id)
    if (!side) return

    const statName = normalize(stat?.type?.name || stat?.type_name || stat?.type || stat?.name)
    const label = getStatAliasLabel(statName)

    if (!label) return
    stats[label][side] = readStatValue(stat)
  })

  const score = getScore(match)
  stats.Goals.home = Math.max(stats.Goals.home, Number(score.home) || 0)
  stats.Goals.away = Math.max(stats.Goals.away, Number(score.away) || 0)

  const yellowCounts = { home: 0, away: 0 }
  const redCounts = { home: 0, away: 0 }
  ;(match?.events || []).forEach((event) => {
    const side = getTeamSide(match, event?.participant_id)
    if (!side) return
    if (normalize(event?.type?.name).includes('yellow')) yellowCounts[side] += 1
    if (normalize(event?.type?.name).includes('red')) redCounts[side] += 1
  })
  stats.Yellowcards.home = Math.max(stats.Yellowcards.home, yellowCounts.home)
  stats.Yellowcards.away = Math.max(stats.Yellowcards.away, yellowCounts.away)
  stats['Red Cards'].home = Math.max(stats['Red Cards'].home, redCounts.home)
  stats['Red Cards'].away = Math.max(stats['Red Cards'].away, redCounts.away)

  return applyFallbackStats(match, stats)
}

const getIndexSnapshot = (match) => {
  const indexes = match?.liveIndexes || {}
  const pressure = indexes.pressureIndex || {}
  const control = indexes.controlIndex || {}
  const resistance = indexes.resistanceIndex || {}
  const volatility = indexes.volatilityIndex || {}

  return {
    minute: getCurrentMinuteNumber(match),
    pi: Math.round(Math.max(Number(pressure.home) || 0, Number(pressure.away) || 0)),
    ci: Math.round(Math.max(Number(control.home) || 0, Number(control.away) || 0)),
    vi: Math.round(Number(volatility.value) || 0),
    ri: Math.round(Math.max(Number(resistance.home) || 0, Number(resistance.away) || 0)),
  }
}

const makeSeededFlow = (match, currentPoint) => {
  const minute = Math.max(Number(currentPoint.minute) || 1, 1)
  const start = Math.max(0, minute - 24)
  const seed = String(match?.id || 'match').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)

  return Array.from({ length: minute - start + 1 }).map((_, index) => {
    const pointMinute = start + index
    const progress = index / Math.max(minute - start, 1)
    const wobble = Math.sin((pointMinute + seed) * 0.55) * 4 + Math.cos((pointMinute + seed) * 0.17) * 3
    return {
      minute: pointMinute,
      pi: Math.round(clamp(currentPoint.pi - 18 + progress * 18 + wobble)),
      ci: Math.round(clamp(currentPoint.ci - 12 + progress * 12 + wobble * 0.7)),
      vi: Math.round(clamp(currentPoint.vi - 10 + progress * 10 - wobble * 0.4)),
      ri: Math.round(clamp(currentPoint.ri - 8 + progress * 8 + wobble * 0.5)),
    }
  })
}

const useMarketFlowHistory = (match) => {
  const [history, setHistory] = useState([])

  useEffect(() => {
    if (!match?.id) return
    const providerHistory = Array.isArray(match?.indexHistory) ? match.indexHistory : []
    if (providerHistory.length) {
      setHistory(
        providerHistory
          .filter((point) => Number.isFinite(Number(point?.minute)))
          .sort((left, right) => Number(left.minute) - Number(right.minute))
      )
      return
    }

    const storageKey = `filterbet_market_flow_${match.id}`
    const currentPoint = getIndexSnapshot(match)

    setHistory(() => {
      let stored = []
      try {
        stored = JSON.parse(localStorage.getItem(storageKey) || '[]')
      } catch (error) {
        stored = []
      }

      const base = stored.length ? stored : makeSeededFlow(match, currentPoint)
      const next = [
        ...base.filter((point) => Number(point.minute) !== Number(currentPoint.minute)),
        currentPoint,
      ]
        .filter((point) => Number.isFinite(Number(point.minute)))
        .sort((left, right) => Number(left.minute) - Number(right.minute))
        .slice(-95)

      localStorage.setItem(storageKey, JSON.stringify(next))
      return next
    })
  }, [match])

  return history
}

const getHistoryMinute = (point) => {
  const directMinute = Number(point?.minuteValue)
  if (Number.isFinite(directMinute)) return directMinute
  const parsedMinute = Number.parseFloat(String(point?.minute || '').replace("'", '.'))
  return Number.isFinite(parsedMinute) ? parsedMinute : 0
}

const buildHistoricalStatEvents = (match, stats, radarMode = false) => {
  if (!match?.id) return []

  const trackedStats = TRACKED_TAPE_STATS.filter((item) => radarMode || !item.radarOnly)
  const history = (Array.isArray(match?.classicHistory) ? match.classicHistory : [])
    .filter((point) => point?.stats && getHistoryMinute(point) >= 0)
    .sort((left, right) => {
      const minuteDiff = getHistoryMinute(left) - getHistoryMinute(right)
      if (minuteDiff !== 0) return minuteDiff
      return new Date(left?.capturedAt || 0).getTime() - new Date(right?.capturedAt || 0).getTime()
    })
  const previousValues = new Map()
  const events = []

  history.forEach((point, pointIndex) => {
    const minute = Math.max(1, Math.round(getHistoryMinute(point)))
    trackedStats.forEach((item, trackedIndex) => {
      ;['home', 'away'].forEach((side, sideIndex) => {
        const key = `${side}:${item.historyKey}`
        const currentValue = Math.max(0, Number(point?.stats?.[side]?.[item.historyKey]) || 0)
        const previousValue = previousValues.get(key) || 0
        const delta = Math.max(0, Math.round(currentValue - previousValue))
        previousValues.set(key, Math.max(previousValue, currentValue))
        if (!delta) return

        events.push({
          id: `stat-history-${match.id}-${key}-${pointIndex}-${Math.round(currentValue)}`,
          minute,
          capturedAt: point?.capturedAt || null,
          sequence: trackedIndex * 2 + sideIndex,
          side,
          title: item.title,
          tone: item.tone,
          icon: item.icon,
          source: point?.estimated ? 'stat-history-estimated' : 'stat-history',
          stat: item.stat,
          delta,
        })
      })
    })
  })

  const currentMinute = Math.max(1, getCurrentMinuteNumber(match))
  trackedStats.forEach((item, trackedIndex) => {
    ;['home', 'away'].forEach((side, sideIndex) => {
      const key = `${side}:${item.historyKey}`
      const currentValue = Math.max(0, Number(stats?.[item.stat]?.[side]) || 0)
      const previousValue = previousValues.get(key) || 0
      const delta = Math.max(0, Math.round(currentValue - previousValue))
      if (!delta) return

      events.push({
        id: `stat-current-${match.id}-${key}-${Math.round(currentValue)}`,
        minute: currentMinute,
        capturedAt: match?.lastUpdate || null,
        sequence: trackedIndex * 2 + sideIndex,
        side,
        title: item.title,
        tone: item.tone,
        icon: item.icon,
        source: 'stat-current',
        stat: item.stat,
        delta,
      })
    })
  })

  return events
}

const useSyntheticEventTape = (match, stats, radarMode = false) => useMemo(
  () => buildHistoricalStatEvents(match, stats, radarMode),
  [match, radarMode, stats]
)

const Sparkline = ({ data, dataKey, color }) => (
  <ResponsiveContainer width="100%" height={42}>
    <AreaChart data={data.slice(-14)} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
      <Area type="monotone" dataKey={dataKey} stroke={color} fill={color} fillOpacity={0.16} strokeWidth={1.8} dot={false} />
    </AreaChart>
  </ResponsiveContainer>
)

const GoalMarker = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} fill="#ffffff" r="8" stroke="#101314" strokeWidth="1.6" />
    <circle cx={cx} cy={cy} fill="#101314" r="2.2" />
    <circle cx={cx - 3.6} cy={cy - 2.8} fill="#101314" r="1.4" />
    <circle cx={cx + 3.6} cy={cy - 2.8} fill="#101314" r="1.4" />
    <circle cx={cx - 3.2} cy={cy + 3.2} fill="#101314" r="1.25" />
    <circle cx={cx + 3.2} cy={cy + 3.2} fill="#101314" r="1.25" />
  </g>
)

const FlowTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="match-flow-tooltip">
      <div className="match-flow-tooltip-title">{label}'</div>
      {payload.map((item) => (
        <div key={item.dataKey} style={{ color: item.color }}>
          {String(item.name).toUpperCase()}: <strong>{Math.round(item.value)}</strong>
        </div>
      ))}
    </div>
  )
}

const buildTapeEvents = (match, syntheticEvents = [], radarMode = false) => {
  const { homeName, awayName } = getParticipants(match)
  const providerEvents = (match?.events || []).map((event) => {
    const side = getTeamSide(match, event?.participant_id)
    const title = event?.type?.name || 'Live event'
    const visual = getTapeEventVisual({ title })
    const riskKinds = ['yellow-card', 'red-card', 'foul', 'offside', 'disallowed-goal', 'big-chance-missed']
    const pressureKinds = ['goal', 'shot-target', 'big-chance', 'corner', 'dangerous-attack', 'key-pass']
    return {
      id: event.id,
      minute: (Number(event.minute) || 0) + (Number(event.extra_minute) || 0),
      side,
      title,
      tone: radarMode
        ? riskKinds.includes(visual.kind) ? 'risk' : pressureKinds.includes(visual.kind) ? 'pressure' : 'neutral'
        : normalize(title).includes('card') ? 'risk' : 'pressure',
      icon: normalize(title).includes('corner')
        ? 'bi-flag'
        : normalize(title).includes('goal')
          ? 'bi-dribbble'
          : normalize(title).includes('card')
            ? 'bi-square-fill'
            : 'bi-activity',
      player: event?.player?.display_name || event?.player?.common_name || event?.player_name || '',
      source: 'provider',
      teamName: side === 'away' ? awayName : homeName,
    }
  })

  const getCategory = (title) => {
    const value = normalize(title)
    if (value.includes('goal')) return 'goal'
    if (value.includes('yellow')) return 'yellow-card'
    if (value.includes('red')) return 'red-card'
    return value
  }
  const normalizedSyntheticEvents = syntheticEvents.map((event) => ({
    ...event,
    teamName: event.side === 'away' ? awayName : homeName,
  }))
  const providerOfficialCounts = providerEvents.reduce((counts, event) => {
    const category = getCategory(event.title)
    if (!['goal', 'yellow-card', 'red-card'].includes(category)) return counts
    const key = `${event.side}:${category}`
    counts.set(key, (counts.get(key) || 0) + 1)
    return counts
  }, new Map())
  const remainingProviderOfficialCounts = new Map(providerOfficialCounts)
  const deduplicatedSyntheticEvents = normalizedSyntheticEvents.flatMap((syntheticEvent) => {
    const syntheticCategory = getCategory(syntheticEvent.title)
    if (!['goal', 'yellow-card', 'red-card'].includes(syntheticCategory)) return [syntheticEvent]
    const key = `${syntheticEvent.side}:${syntheticCategory}`
    const eventDelta = Math.max(1, Math.round(Number(syntheticEvent.delta) || 1))
    const providerDuplicates = Math.min(eventDelta, remainingProviderOfficialCounts.get(key) || 0)
    remainingProviderOfficialCounts.set(key, Math.max(0, (remainingProviderOfficialCounts.get(key) || 0) - providerDuplicates))
    const remainingDelta = eventDelta - providerDuplicates
    return remainingDelta > 0 ? [{ ...syntheticEvent, delta: remainingDelta }] : []
  })

  return [...deduplicatedSyntheticEvents, ...providerEvents]
    .sort((left, right) => Number(right.minute) - Number(left.minute))
}

const CLASSIC_WINDOWS = [
  { id: 'total', label: 'TOTAL' },
  { id: 'firstHalf', label: '1A PARTE' },
  { id: 'secondHalf', label: '2A PARTE' },
  { id: 'last5', label: '5 MIN' },
  { id: 'last10', label: '10 MIN' },
]

const CLASSIC_STAT_ROWS = [
  { label: 'Escanteios', icon: 'bi-flag', key: 'Corners', profileKey: 'corners', format: (value) => Math.round(value) },
  { label: 'Cartoes Amarelos', icon: 'bi-square-fill yellow', key: 'Yellowcards', profileKey: 'yellowCards', format: (value) => Math.round(value) },
  { label: 'Cartoes Vermelhos', icon: 'bi-square-fill red', key: 'Red Cards', profileKey: 'redCards', format: (value) => Math.round(value) },
  { label: 'Ataques Perigosos', icon: 'bi-life-preserver', key: 'Dangerous Attacks', profileKey: 'dangerousAttacks', format: (value) => Math.round(value) },
  { label: 'Finalizacoes', icon: 'bi-broadcast-pin', key: 'Shots Total', profileKey: 'shotsTotal', format: (value) => Math.round(value) },
  { label: 'Finalizacoes no Alvo', icon: 'bi-pencil-fill', key: 'Shots On Target', profileKey: 'shotsOnTarget', format: (value) => Math.round(value) },
  { label: 'Posse de Bola', icon: 'bi-bar-chart-fill', key: 'Ball Possession %', profileKey: 'possession', format: (value) => `${Math.round(value)}%` },
]

const getClassicClock = (match) => {
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const currentPeriod = [...periods]
    .filter((period) => Number(period?.minutes) || Number(period?.seconds))
    .sort((left, right) => (Number(left?.sort_order) || 0) - (Number(right?.sort_order) || 0))
    .pop()
  const minute = Number(currentPeriod?.minutes) || getCurrentMinuteNumber(match)
  const second = Number(currentPeriod?.seconds) || 0
  const extra = Number(currentPeriod?.time_added) || 0

  return {
    minuteValue: Math.max(1, minute || 1),
    second,
    label: extra > 0
      ? `${minute}+${extra}'${String(second).padStart(2, '0')}`
      : `${minute}'${String(second).padStart(2, '0')}`,
  }
}

const getClassicSideStats = (match, stats, side) => {
  const profile = getTeamStatProfile(match, side, 'total')
  const score = getScore(match)
  const goals = side === 'home' ? Number(score.home) || 0 : Number(score.away) || 0

  return {
    goals,
    xg: Number(profile.xg || 0),
    possession: Number(stats['Ball Possession %']?.[side]) || Number(profile.possession) || 0,
    corners: Number(stats.Corners?.[side]) || Number(profile.corners) || 0,
    shotsTotal: Number(stats['Shots Total']?.[side]) || Number(profile.shotsTotal) || 0,
    shotsOnTarget: Number(stats['Shots On Target']?.[side]) || Number(profile.shotsOnTarget) || 0,
    dangerousAttacks: Number(stats['Dangerous Attacks']?.[side]) || Number(profile.dangerousAttacks) || 0,
    fouls: Number(stats.Fouls?.[side]) || Number(profile.fouls) || 0,
    yellowCards: Number(stats.Yellowcards?.[side]) || Number(profile.yellowCards) || 0,
    redCards: Number(stats['Red Cards']?.[side]) || 0,
  }
}

const pickClassicEvent = (currentStats, previousStats) => {
  if (!previousStats) return null
  if (currentStats.goals > previousStats.goals) return { type: 'goal', label: 'golo' }
  if (currentStats.redCards > previousStats.redCards) return { type: 'red_card', label: 'vermelho' }
  if (currentStats.yellowCards > previousStats.yellowCards) return { type: 'yellow_card', label: 'amarelo' }
  if (
    currentStats.shotsOnTarget > previousStats.shotsOnTarget ||
    currentStats.dangerousAttacks > previousStats.dangerousAttacks
  ) {
    return { type: 'dangerous_shot', label: 'remate perigoso' }
  }
  if (currentStats.shotsTotal > previousStats.shotsTotal) return { type: 'shot', label: 'remate' }
  if (currentStats.fouls > previousStats.fouls) return { type: 'foul', label: 'falta' }
  return null
}

const buildClassicSnapshot = (match, stats, previousPoint) => {
  const clock = getClassicClock(match)
  const pressure = match?.liveIndexes?.pressureIndex || {}
  const sideStats = {
    home: getClassicSideStats(match, stats, 'home'),
    away: getClassicSideStats(match, stats, 'away'),
  }
  const homeXg = sideStats.home.xg || previousPoint?.home?.xg || 0
  const awayXg = sideStats.away.xg || previousPoint?.away?.xg || 0

  return {
    minute: clock.label,
    minuteValue: clock.minuteValue,
    second: clock.second,
    capturedAt: new Date().toISOString(),
    home: {
      event: pickClassicEvent(sideStats.home, previousPoint?.stats?.home),
      pressure: Math.round(Number(pressure.home) || 0),
      xg: homeXg,
      possession: sideStats.home.possession,
    },
    away: {
      event: pickClassicEvent(sideStats.away, previousPoint?.stats?.away),
      pressure: Math.round(Number(pressure.away) || 0),
      xg: awayXg,
      possession: sideStats.away.possession,
    },
    stats: sideStats,
  }
}

const useClassicHistory = (match, stats) => {
  const [history, setHistory] = useState([])

  useEffect(() => {
    if (!match?.id) return
    const providerHistory = Array.isArray(match?.classicHistory) ? match.classicHistory : []
    if (providerHistory.length) {
      setHistory(providerHistory)
      return
    }

    const storageKey = `filterbet_classic_history_${match.id}`
    setHistory(() => {
      let stored = []
      try {
        stored = JSON.parse(localStorage.getItem(storageKey) || '[]')
      } catch (error) {
        stored = []
      }

      const lastPoint = stored[stored.length - 1]
      const lastCapturedAt = lastPoint?.capturedAt ? new Date(lastPoint.capturedAt).getTime() : 0
      if (lastCapturedAt && Date.now() - lastCapturedAt < 15000) return stored

      const next = [...stored, buildClassicSnapshot(match, stats, lastPoint)].slice(-320)
      localStorage.setItem(storageKey, JSON.stringify(next))
      return next
    })
  }, [match, stats])

  return history
}

const getClassicWindowRows = (history, windowId) => {
  const rows = Array.isArray(history) ? history : []
  if (windowId === 'firstHalf') return rows.filter((row) => Number(row?.minuteValue) <= 45)
  if (windowId === 'secondHalf') return rows.filter((row) => Number(row?.minuteValue) > 45)
  if (windowId === 'last5' || windowId === 'last10') {
    const latestMinute = rows.reduce((highest, row) => Math.max(highest, Number(row?.minuteValue) || 0), 0)
    const windowSize = windowId === 'last5' ? 5 : 10
    return rows.filter((row) => Number(row?.minuteValue) >= Math.max(0, latestMinute - windowSize))
  }
  return rows
}

const getClassicTimedRows = (history, windowId, intervalMs = 20000) => {
  const sortedRows = [...getClassicWindowRows(history, windowId)]
    .filter((row) => Number.isFinite(Number(row?.minuteValue)))
    .sort((left, right) => {
      const leftMinute = Number(left?.minuteValue) || 0
      const rightMinute = Number(right?.minuteValue) || 0
      if (leftMinute !== rightMinute) return leftMinute - rightMinute
      return new Date(left?.capturedAt || 0).getTime() - new Date(right?.capturedAt || 0).getTime()
    })

  const result = []
  let lastCapturedAt = 0
  let lastMinute = null

  sortedRows.forEach((row) => {
    const capturedAt = new Date(row?.capturedAt || 0).getTime()
    const minute = Math.floor(Number(row?.minuteValue) || 0)
    const shouldKeep =
      !result.length ||
      (capturedAt && lastCapturedAt && capturedAt - lastCapturedAt >= intervalMs) ||
      (!capturedAt && minute !== lastMinute)

    if (!shouldKeep) return
    result.push(row)
    if (capturedAt) lastCapturedAt = capturedAt
    lastMinute = minute
  })

  return result
}

const getClassicPreciseMinute = (row) =>
  Math.max(0, (Number(row?.minuteValue) || 0) + (Number(row?.second) || 0) / 60)

const getClassicChartFrame = (history) => {
  const allRows = (Array.isArray(history) ? history : []).filter((row) => Number.isFinite(Number(row?.minuteValue)))
  const latestMinute = allRows.reduce((highest, row) => Math.max(highest, getClassicPreciseMinute(row)), 0)

  return { start: 0, end: Math.max(1, latestMinute || 1) }
}

const getClassicWindowBaselineRow = (history, windowId) => {
  const rows = [...(Array.isArray(history) ? history : [])].sort(
    (left, right) => Number(left?.minuteValue) - Number(right?.minuteValue)
  )

  if (!rows.length || windowId === 'total') return null
  if (windowId === 'secondHalf') {
    return [...rows]
      .filter((row) => Number(row?.minuteValue) <= 45)
      .sort((left, right) => Number(right?.minuteValue) - Number(left?.minuteValue))[0] || null
  }
  if (windowId === 'last5' || windowId === 'last10') {
    const latestMinute = rows.reduce((highest, row) => Math.max(highest, Number(row?.minuteValue) || 0), 0)
    const windowSize = windowId === 'last5' ? 5 : 10
    return [...rows]
      .filter((row) => Number(row?.minuteValue) <= Math.max(0, latestMinute - windowSize))
      .sort((left, right) => Number(right?.minuteValue) - Number(left?.minuteValue))[0] || null
  }
  return null
}

const makeClassicDisplayStats = () =>
  CLASSIC_STAT_ROWS.reduce((accumulator, row) => {
    accumulator[row.key] = { home: 0, away: 0 }
    return accumulator
  }, {})

const setClassicDisplaySide = (target, side, values) => {
  CLASSIC_STAT_ROWS.forEach((row) => {
    target[row.key][side] = Number(values?.[row.profileKey]) || 0
  })
}

const buildProfileDisplayStats = (match, windowId) => {
  const nextStats = makeClassicDisplayStats()
  setClassicDisplaySide(nextStats, 'home', getTeamStatProfile(match, 'home', windowId))
  setClassicDisplaySide(nextStats, 'away', getTeamStatProfile(match, 'away', windowId))
  return nextStats
}

const buildHistoryDisplayStats = (historyRows, history, fallbackStats, windowId) => {
  if (!historyRows.length) return fallbackStats

  const sortedRows = [...historyRows].sort((left, right) => {
    const leftMinute = Number(left?.minuteValue) || 0
    const rightMinute = Number(right?.minuteValue) || 0
    if (leftMinute !== rightMinute) return leftMinute - rightMinute
    return new Date(left?.capturedAt || 0).getTime() - new Date(right?.capturedAt || 0).getTime()
  })
  const last = sortedRows[sortedRows.length - 1]
  const baseline = getClassicWindowBaselineRow(history, windowId)
  const nextStats = makeClassicDisplayStats()

  ;['home', 'away'].forEach((side) => {
    const baselineStats = baseline?.stats?.[side] || {}
    const lastStats = last?.stats?.[side] || {}
    CLASSIC_STAT_ROWS.forEach((row) => {
      if (row.profileKey === 'possession') {
        const possessionSamples = sortedRows
          .map((entry) => Number(entry?.[side]?.possession ?? entry?.stats?.[side]?.possession))
          .filter((value) => Number.isFinite(value))
        nextStats[row.key][side] = possessionSamples.length
          ? possessionSamples.reduce((sum, value) => sum + value, 0) / possessionSamples.length
          : Number(last?.[side]?.possession) || Number(lastStats.possession) || 0
        return
      }
      nextStats[row.key][side] = Math.max(
        0,
        (Number(lastStats[row.profileKey]) || 0) - (Number(baselineStats[row.profileKey]) || 0)
      )
    })
  })

  return nextStats
}

const buildClassicDisplayStats = (match, stats, history, windowId) => {
  const profileStats = buildProfileDisplayStats(match, windowId)
  if (windowId === 'total') return profileStats
  return buildHistoryDisplayStats(getClassicWindowRows(history, windowId), history, profileStats, windowId)
}

const buildClassicChartData = (history) => {
  const frame = getClassicChartFrame(history)
  const sourceRows = (Array.isArray(history) ? history : [])
    .filter((point) => Number.isFinite(Number(point?.home?.pressure)) || Number.isFinite(Number(point?.away?.pressure)))
    .sort((left, right) => getClassicPreciseMinute(left) - getClassicPreciseMinute(right))

  if (!sourceRows.length) {
    return [{
      minute: "0'",
      minuteValue: 0,
      second: 0,
      slot: 0,
      homePressure: 0,
      awayPressure: 0,
      home: { pressure: 0, event: null },
      away: { pressure: 0, event: null },
    }]
  }

  let cursor = 0
  let currentSource = sourceRows[0]
  const chartEnd = Math.max(1, Math.ceil(frame.end))

  return Array.from({ length: chartEnd + 1 }).map((_, minute) => {
    while (
      cursor < sourceRows.length &&
      getClassicPreciseMinute(sourceRows[cursor]) <= minute
    ) {
      currentSource = sourceRows[cursor]
      cursor += 1
    }

    return {
      ...currentSource,
      minute: `${minute}'`,
      minuteValue: minute,
      second: 0,
      slot: minute,
      home: { ...currentSource.home, event: null },
      away: { ...currentSource.away, event: null },
      homePressure: Math.max(0, Number(currentSource?.home?.pressure) || 0),
      awayPressure: -Math.max(0, Number(currentSource?.away?.pressure) || 0),
    }
  })
}

const mapMinuteToClassicSlot = (minute, data, frame) => {
  if (!data.length) return 0
  const nearest = data.reduce((best, point) => {
    const diff = Math.abs((Number(point?.minuteValue) || 0) - minute)
    return diff < best.diff ? { diff, slot: point.slot } : best
  }, { diff: Number.POSITIVE_INFINITY, slot: 0 })

  if (Number.isFinite(nearest.diff) && nearest.diff <= 2) return nearest.slot

  const chartStart = Number(frame?.start) || 0
  const maxSlot = Math.max((Number(frame?.end) || chartStart + 1) - chartStart, 1)
  return clamp(minute - chartStart, 0, maxSlot)
}

const buildProviderClassicEventDots = (match, data, frame) =>
  (match?.events || [])
    .map((event) => {
      const eventName = normalize(event?.type?.name)
      const type = eventName.includes('goal') ? 'goal' : null
      const side = getTeamSide(match, event?.participant_id)
      const minute = (Number(event?.minute) || 0) + (Number(event?.extra_minute) || 0)
      if (!type || !side || !minute) return null
      return {
        id: `provider-${event.id || `${side}-${type}-${minute}`}`,
        side,
        type,
        slot: mapMinuteToClassicSlot(minute, data, frame),
        y: side === 'home' ? 92 : -92,
      }
    })
    .filter(Boolean)

const buildMarketGoalEvents = (match) => {
  const score = getScore(match)
  const maxBySide = {
    home: Number(score.home) || 0,
    away: Number(score.away) || 0,
  }
  const seen = new Set()
  const counts = { home: 0, away: 0 }

  return (match?.events || [])
    .map((event) => {
      const eventName = normalize(event?.type?.name)
      const side = getTeamSide(match, event?.participant_id)
      const minute = (Number(event?.minute) || 0) + (Number(event?.extra_minute) || 0)
      if (!eventName.includes('goal') || eventName.includes('disallowed') || !side || !minute) return null
      const player = normalize(event?.player?.display_name || event?.player?.common_name || event?.player_name)
      const key = event?.id ? `provider:${event.id}` : `${side}:${minute}:${player || 'goal'}`
      if (seen.has(key)) return null
      seen.add(key)
      return {
        id: key,
        minute,
        side,
      }
    })
    .filter(Boolean)
    .sort((left, right) => left.minute - right.minute)
    .filter((event) => {
      if (counts[event.side] >= maxBySide[event.side]) return false
      counts[event.side] += 1
      return true
    })
}

const getFlowPointValue = (point, key, side) => {
  if (side === 'all') return Number(point?.[key]) || 0
  const sideSuffix = side === 'home' ? 'Home' : 'Away'
  return Number(point?.[`${key}${sideSuffix}`]) || Number(point?.[key]) || 0
}

const buildFlowSeriesHistory = (history, side) =>
  (Array.isArray(history) ? history : []).map((point) => {
    if (side === 'both') {
      return INDEX_SERIES.reduce((nextPoint, series) => ({
        ...nextPoint,
        [`${series.id}Home`]: getFlowPointValue(point, series.id, 'home'),
        [`${series.id}Away`]: getFlowPointValue(point, series.id, 'away'),
      }), { ...point })
    }

    return {
      ...point,
      pi: getFlowPointValue(point, 'pi', side),
      ci: getFlowPointValue(point, 'ci', side),
      vi: getFlowPointValue(point, 'vi', side),
      ri: getFlowPointValue(point, 'ri', side),
    }
  })

const getFlowCurrentSnapshot = (match, side) => {
  if (side === 'all') return getIndexSnapshot(match)

  const indexes = match?.liveIndexes || {}
  return {
    minute: getCurrentMinuteNumber(match),
    pi: Math.round(Number(indexes?.pressureIndex?.[side]) || 0),
    ci: Math.round(Number(indexes?.controlIndex?.[side]) || 0),
    vi: Math.round(Number(getTeamIndexValue(match, side, 'vi')) || 0),
    ri: Math.round(Number(indexes?.resistanceIndex?.[side]) || 0),
  }
}

const MarketFlow = ({ match, history, radarMode = false }) => {
  const isCompact = useCompactViewport()
  const isMobile = useCompactViewport(767)
  const [activeSeries, setActiveSeries] = useState(INDEX_SERIES.map((series) => series.id))
  const [chartMode, setChartMode] = useState('line')
  const [selectedSide, setSelectedSide] = useState(() => radarMode ? 'both' : 'home')
  const { homeName, awayName } = getParticipants(match)
  const flowHistory = useMemo(() => buildFlowSeriesHistory(history, selectedSide), [history, selectedSide])
  const current = useMemo(
    () => getFlowCurrentSnapshot(match, selectedSide === 'both' ? 'all' : selectedSide),
    [match, selectedSide]
  )
  const chartSeries = useMemo(() => {
    const shortHome = String(homeName || tr('Home', 'Casa')).split(/\s+/).slice(0, 2).join(' ')
    const shortAway = String(awayName || tr('Away', 'Fora')).split(/\s+/).slice(0, 2).join(' ')

    return INDEX_SERIES.flatMap((series) => {
      if (!activeSeries.includes(series.id)) return []
      if (selectedSide !== 'both') {
        return [{ ...series, dataKey: series.id, name: series.short }]
      }
      return [
        { ...series, dataKey: `${series.id}Home`, name: `${series.short} · ${shortHome}`, team: 'home' },
        { ...series, dataKey: `${series.id}Away`, name: `${series.short} · ${shortAway}`, team: 'away' },
      ]
    })
  }, [activeSeries, awayName, homeName, selectedSide])
  const lastTenStart = Math.max(0, flowHistory.length - 10)
  const lastTen = flowHistory.slice(lastTenStart)
  const chartEndMinute = getChartEndMinute(match, flowHistory)
  const chartTicks = getChartTicks(chartEndMinute)
  const flowChartHeight = isMobile ? 284 : isCompact ? 320 : 560
  const goalEvents = useMemo(() => buildMarketGoalEvents(match), [match])

  const toggleSeries = (seriesId) => {
    setActiveSeries((currentSeries) => {
      if (currentSeries.includes(seriesId) && currentSeries.length > 1) {
        return currentSeries.filter((id) => id !== seriesId)
      }
      if (currentSeries.includes(seriesId)) return currentSeries
      return [...currentSeries, seriesId]
    })
  }

  return (
    <section className="match-detail-panel market-flow-panel">
      <div className="match-detail-panel-header">
        <div>
          <span className="live-panel-label">{tr('Market Flow')}</span>
          <strong>{tr('Live Market Flow')}</strong>
        </div>
        <div className="market-flow-controls">
          <div className="market-flow-control-group" data-label="Modo">
            <button
              type="button"
              className={chartMode === 'line' ? 'active' : ''}
              onClick={() => setChartMode('line')}
            >
              Linha
            </button>
            <button
              type="button"
              className={chartMode === 'bar' ? 'active' : ''}
              onClick={() => setChartMode('bar')}
            >
              Coluna
            </button>
          </div>
          <div className="market-flow-control-group" data-label="Equipa">
            <button
              type="button"
              className={selectedSide === 'home' ? 'active' : ''}
              onClick={() => setSelectedSide('home')}
            >
              {String(homeName || 'Casa').split(/\s+/).slice(0, 2).join(' ')}
            </button>
            <button
              type="button"
              className={selectedSide === 'away' ? 'active' : ''}
              onClick={() => setSelectedSide('away')}
            >
              {String(awayName || 'Fora').split(/\s+/).slice(0, 2).join(' ')}
            </button>
            {radarMode && (
              <button
                type="button"
                className={selectedSide === 'both' ? 'active' : ''}
                onClick={() => setSelectedSide('both')}
              >
                {tr('Both', 'Ambas')}
              </button>
            )}
          </div>
          <div className="market-flow-control-group" data-label="Indices">
            {INDEX_SERIES.map((series) => (
              <button
                type="button"
                className={activeSeries.includes(series.id) ? 'active' : ''}
                key={series.id}
                onClick={() => toggleSeries(series.id)}
              >
                {series.short}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="market-flow-grid">
        <div className="market-flow-chart">
          <ResponsiveContainer width="100%" height={flowChartHeight}>
            {chartMode === 'bar' ? (
              <BarChart data={flowHistory} margin={{ top: 12, right: 18, left: -10, bottom: 2 }}>
                <CartesianGrid stroke="#22282a" strokeDasharray="2 4" />
                <XAxis
                  allowDataOverflow
                  dataKey="minute"
                  domain={[0, chartEndMinute]}
                  tick={{ fill: '#87918b', fontSize: 10 }}
                  tickFormatter={(value) => `${value}'`}
                  ticks={chartTicks}
                  type="number"
                />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fill: '#87918b', fontSize: 10 }} />
                <ReferenceLine y={50} stroke="#3d4544" strokeDasharray="3 4" />
                <ReferenceLine x={45} stroke="#5b6262" strokeDasharray="3 4" />
                <Tooltip content={<FlowTooltip />} />
                {goalEvents.map((event) => (
                  <ReferenceDot
                    fill="transparent"
                    isFront
                    key={event.id}
                    r={0}
                    shape={<GoalMarker />}
                    x={event.minute}
                    y={96}
                  />
                ))}
                {chartSeries.map((series) => (
                  <Bar
                    barSize={selectedSide === 'both' ? 5 : 8}
                    dataKey={series.dataKey}
                    fill={series.color}
                    fillOpacity={series.team === 'away' ? 0.42 : 0.92}
                    key={series.dataKey}
                    name={series.name}
                    radius={[2, 2, 0, 0]}
                  />
                ))}
              </BarChart>
            ) : (
              <ComposedChart data={flowHistory} margin={{ top: 12, right: 18, left: -10, bottom: 2 }}>
                <defs>
                  <linearGradient id="flowGreen" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#19f05a" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#19f05a" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#22282a" strokeDasharray="2 4" />
                <XAxis
                  allowDataOverflow
                  dataKey="minute"
                  domain={[0, chartEndMinute]}
                  tick={{ fill: '#87918b', fontSize: 10 }}
                  tickFormatter={(value) => `${value}'`}
                  ticks={chartTicks}
                  type="number"
                />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fill: '#87918b', fontSize: 10 }} />
                <ReferenceLine y={50} stroke="#3d4544" strokeDasharray="3 4" />
                <ReferenceLine x={45} stroke="#5b6262" strokeDasharray="3 4" label={{ value: 'HT', fill: '#8d9690', fontSize: 10 }} />
                <Tooltip content={<FlowTooltip />} />
                {goalEvents.map((event) => (
                  <ReferenceDot
                    fill="transparent"
                    isFront
                    key={event.id}
                    r={0}
                    shape={<GoalMarker />}
                    x={event.minute}
                    y={96}
                  />
                ))}
                {chartSeries.map((series) => (
                    <React.Fragment key={series.dataKey}>
                      {selectedSide !== 'both' && series.id === 'pi' && (
                        <Area
                          dataKey={series.dataKey}
                          fill="url(#flowGreen)"
                          fillOpacity={1}
                          stroke="transparent"
                          type="stepAfter"
                        />
                      )}
                      <Line
                        activeDot={{ r: 4, strokeWidth: 2 }}
                        dataKey={series.dataKey}
                        dot={false}
                        name={series.name}
                        stroke={series.color}
                        strokeDasharray={series.team === 'away' ? '6 4' : undefined}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeOpacity={series.team === 'away' ? 0.72 : 1}
                        strokeWidth={series.team ? 2.1 : 2.6}
                        type="stepAfter"
                      />
                    </React.Fragment>
                ))}
              </ComposedChart>
            )}
          </ResponsiveContainer>
          <div className="market-flow-legend">
            {INDEX_SERIES.map((series) => (
              <span key={series.id}>
                <i style={{ background: series.color }} />
                {tr(series.label)}
              </span>
            ))}
            {selectedSide === 'both' && (
              <>
                <span className="market-flow-team-legend home"><i />{homeName}</span>
                <span className="market-flow-team-legend away"><i />{awayName}</span>
              </>
            )}
          </div>
        </div>

        <div className="market-flow-cards">
          {[
            { key: 'pi', label: 'Pressure Index', value: current.pi, note: selectedSide === 'both' ? tr('Both teams', 'Ambas as equipas') : tr('Selected team', 'Equipe selecionada'), color: '#19f05a' },
            { key: 'ci', label: 'Control Index', value: current.ci, note: selectedSide === 'both' ? tr('Both teams', 'Ambas as equipas') : tr('Selected team', 'Equipe selecionada'), color: '#f2f4f7' },
            { key: 'vi', label: 'Volatility Index', value: current.vi, note: tr(match?.liveIndexes?.volatilityIndex?.label || 'Live'), color: '#ffd247' },
            { key: 'ri', label: 'Resistance Index', value: current.ri, note: selectedSide === 'both' ? tr('Both teams', 'Ambas as equipas') : tr('Selected team', 'Equipe selecionada'), color: '#ff4b3e' },
          ].map((card) => {
            const oldValue = Number(lastTen[0]?.[card.key]) || card.value
            const delta = Math.round(card.value - oldValue)
            return (
              <div className="market-flow-card" key={card.key}>
                <span>{tr(card.label)}</span>
                <div className="market-flow-card-value">
                  {card.value}
                  <small className={delta >= 0 ? 'positive' : 'negative'}>{delta >= 0 ? '+' : ''}{delta}</small>
                </div>
                <Sparkline data={flowHistory} dataKey={card.key} color={card.color} />
                <strong>{card.note}</strong>
              </div>
            )
          })}
        </div>
      </div>
      <div className="market-flow-telegram-cta">
        <TelegramPromoCta context="live_pressure_chart" />
      </div>
    </section>
  )
}

const ClassicTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const point = payload[0]?.payload || {}
  return (
    <div className="match-flow-tooltip">
      <div className="match-flow-tooltip-title">{point.minute}</div>
      <div style={{ color: '#19f05a' }}>{tr('Home', 'Casa')} PI: <strong>{Math.round(point.homePressure || 0)}</strong></div>
      <div style={{ color: '#ff332f' }}>{tr('Away', 'Fora')} PI: <strong>{Math.round(Math.abs(point.awayPressure || 0))}</strong></div>
    </div>
  )
}

const ClassicPressureChart = ({ history, match }) => {
  const isMobile = useCompactViewport(767)
  const frame = getClassicChartFrame(history)
  const data = buildClassicChartData(history)
  const chartEnd = Math.max(1, frame.end - frame.start)
  const middleTick = Math.round((chartEnd / 2) * 10) / 10
  const chartTicks = Array.from(new Set([0, middleTick, Math.round(chartEnd * 10) / 10]))
  const halftimeSlot = 45 - frame.start
  const showHalftime = frame.start < 45 && frame.end >= 45
  const eventDots = buildProviderClassicEventDots(match, data, frame)
  const tickFormatter = (value) => {
    const numericValue = Number(value) || 0
    return `${Math.round(frame.start + numericValue)}'`
  }

  return (
    <section className="classic-pressure-panel">
      {data.length ? (
        <ResponsiveContainer width="100%" height={isMobile ? 204 : 188}>
          <BarChart
            barCategoryGap="22%"
            data={data}
            margin={isMobile ? { top: 16, right: 8, left: 8, bottom: 10 } : { top: 18, right: 18, left: 18, bottom: 14 }}
          >
            <CartesianGrid stroke="#182329" strokeDasharray="2 6" vertical={false} />
            <XAxis
              allowDataOverflow
              dataKey="slot"
              domain={[0, chartEnd]}
              interval={0}
              tick={{ fill: '#8ea19b', fontSize: isMobile ? 9 : 10 }}
              tickFormatter={tickFormatter}
              ticks={chartTicks}
              type="number"
            />
            <YAxis hide domain={[-100, 100]} />
            <ReferenceLine y={0} stroke="#344045" strokeWidth={1.5} />
            {showHalftime && (
              <ReferenceLine
                x={halftimeSlot}
                stroke="#657174"
                strokeDasharray="3 4"
                label={isMobile ? undefined : { value: "45'", fill: '#9aa5a0', fontSize: 10, position: 'insideTop' }}
              />
            )}
            <Tooltip content={<ClassicTooltip />} />
            <Bar barSize={isMobile ? 6 : 5} dataKey="homePressure" fill="#1fe85a" isAnimationActive={false} radius={[2, 2, 0, 0]} />
            <Bar barSize={isMobile ? 6 : 5} dataKey="awayPressure" fill="#ff332f" isAnimationActive={false} radius={[0, 0, 2, 2]} />
            {eventDots.map((event) => (
              <ReferenceDot
                isFront
                key={event.id}
                r={0}
                shape={<GoalMarker />}
                x={event.slot}
                y={event.y}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="classic-pressure-empty">A aguardar o primeiro snapshot real da Sportmonks.</div>
      )}
    </section>
  )
}

const ClassicStatsStrip = ({ activeWindow, displayStats, onWindowChange }) => {
  return (
    <section className="classic-stats-panel">
      <div className="classic-period-tabs">
        {CLASSIC_WINDOWS.map((window) => (
          <button
            className={activeWindow === window.id ? 'active' : ''}
            key={window.id}
            onClick={() => onWindowChange(window.id)}
            type="button"
          >
            {window.label}
          </button>
        ))}
      </div>
      <div className="classic-stat-list">
        {CLASSIC_STAT_ROWS.map((row) => (
          <div className="classic-stat-row" key={row.label}>
            <strong className="home">{row.format(Number(displayStats[row.key]?.home) || 0)}</strong>
            <span><i className={`bi ${row.icon}`} />{row.label}</span>
            <strong className="away">{row.format(Number(displayStats[row.key]?.away) || 0)}</strong>
          </div>
        ))}
      </div>
    </section>
  )
}

const ClassicHistoryTable = ({ activeWindow, history, match }) => {
  const rows = [...getClassicTimedRows(history, activeWindow)].reverse()
  const currentHomeXg = Number(getTeamStatProfile(match, 'home', 'total')?.xg) || 0
  const currentAwayXg = Number(getTeamStatProfile(match, 'away', 'total')?.xg) || 0

  return (
    <section className="classic-history-panel">
      <div className="classic-history-head">
        <span>Min</span>
        <span>PI</span>
        <span>xG</span>
        <span>%</span>
        <span>-</span>
        <span>%</span>
        <span>xG</span>
        <span>PI</span>
      </div>
      <div className="classic-history-body">
        {rows.map((row, index) => {
          const homeXg = index === 0 ? currentHomeXg : Number(row.home?.xg || 0)
          const awayXg = index === 0 ? currentAwayXg : Number(row.away?.xg || 0)

          return (
          <div className="classic-history-row" key={`${row.capturedAt || row.minute}-${index}`}>
            <strong>{row.minute || '--'}</strong>
            <strong className="classic-pi-value home">{Math.round(Number(row.home?.pressure) || 0)}</strong>
            <span className="classic-xg-value">{homeXg.toFixed(2)}</span>
            <span className="classic-possession-value home">{Number(row.home?.possession || 0).toFixed(1)}%</span>
            <span className="classic-divider">-</span>
            <span className="classic-possession-value away">{Number(row.away?.possession || 0).toFixed(1)}%</span>
            <span className="classic-xg-value">{awayXg.toFixed(2)}</span>
            <strong className="classic-pi-value away">{Math.round(Number(row.away?.pressure) || 0)}</strong>
          </div>
          )
        })}
        {!rows.length && <div className="match-detail-empty">{tr('Saving live snapshots every 20 seconds.', 'Salvando snapshots ao vivo a cada 20 segundos.')}</div>}
      </div>
      <div className="classic-history-mobile-list">
        {rows.map((row, index) => {
          const homeXg = index === 0 ? currentHomeXg : Number(row.home?.xg || 0)
          const awayXg = index === 0 ? currentAwayXg : Number(row.away?.xg || 0)

          return (
            <article className="classic-history-mobile-card" key={`mobile-${row.capturedAt || row.minute}-${index}`}>
              <div className="classic-history-mobile-top">
                <span>Snapshot</span>
                <strong>{row.minute || '--'}</strong>
              </div>
              <div className="classic-history-mobile-grid">
                <div className="classic-history-mobile-side home">
                  <div className="classic-history-mobile-side-title">{tr('Home', 'Casa')}</div>
                  <div className="classic-history-mobile-metric">
                    <span>PI</span>
                    <strong>{Math.round(Number(row.home?.pressure) || 0)}</strong>
                  </div>
                  <div className="classic-history-mobile-metric">
                    <span>xG</span>
                    <strong>{homeXg.toFixed(2)}</strong>
                  </div>
                  <div className="classic-history-mobile-metric">
                    <span>Posse</span>
                    <strong>{Number(row.home?.possession || 0).toFixed(1)}%</strong>
                  </div>
                </div>
                <div className="classic-history-mobile-side away">
                  <div className="classic-history-mobile-side-title">{tr('Away', 'Fora')}</div>
                  <div className="classic-history-mobile-metric">
                    <span>PI</span>
                    <strong>{Math.round(Number(row.away?.pressure) || 0)}</strong>
                  </div>
                  <div className="classic-history-mobile-metric">
                    <span>xG</span>
                    <strong>{awayXg.toFixed(2)}</strong>
                  </div>
                  <div className="classic-history-mobile-metric">
                    <span>Posse</span>
                    <strong>{Number(row.away?.possession || 0).toFixed(1)}%</strong>
                  </div>
                </div>
              </div>
            </article>
          )
        })}
        {!rows.length && <div className="match-detail-empty">{tr('Saving live snapshots every 20 seconds.', 'Salvando snapshots ao vivo a cada 20 segundos.')}</div>}
      </div>
    </section>
  )
}

const ClassicView = ({ history, match, stats }) => {
  const [activeWindow, setActiveWindow] = useState('total')
  const displayStats = useMemo(
    () => buildClassicDisplayStats(match, stats, history, activeWindow),
    [activeWindow, history, match, stats]
  )

  return (
    <section className="classic-view-shell">
      <ClassicPressureChart history={history} match={match} />
      <ClassicStatsStrip activeWindow={activeWindow} displayStats={displayStats} onWindowChange={setActiveWindow} />
      <ClassicHistoryTable activeWindow={activeWindow} history={history} match={match} />
    </section>
  )
}

const EventTape = ({ events, professionalIcons = false }) => {
  return (
    <section className="match-detail-panel event-tape-panel">
      <div className="match-detail-panel-header compact">
        <span className="live-panel-label">{tr('Event Tape')} <small>({tr('Live', 'Ao vivo').toLowerCase()})</small></span>
        <span className="live-panel-label">{events.length} {tr('Events', 'eventos')}</span>
      </div>
      <div className="event-tape-list">
        {events.map((event) => {
          const eventCopy = getTapeEventCopy(event)
          const eventVisual = getTapeEventVisual(event)
          const deltaSuffix = event.delta > 1 ? ` Foram detetados ${event.delta} novos registos.` : ''
          const playerSuffix = event.player ? ` Jogador: ${event.player}.` : ''
          return (
            <div className={`event-tape-row tone-${event.tone}`} key={event.id}>
              <span className="event-minute">{event.minute || '--'}'</span>
              <span className={`event-tape-symbol event-${eventVisual.kind}`} aria-hidden="true">
                {professionalIcons
                  ? <TapeEventIcon kind={eventVisual.kind} />
                  : getLegacyTapeEventSymbol(event, eventVisual)}
              </span>
              <div>
                <strong>{eventCopy.title} - {event.teamName}</strong>
                <p>{eventCopy.description}{deltaSuffix}{playerSuffix}</p>
              </div>
              <span>
                {event.tone === 'risk'
                  ? tr('Risk', 'Risco')
                  : event.tone === 'neutral'
                    ? tr('Activity', 'Atividade')
                    : tr('Pressure', 'Pressão')} +
              </span>
            </div>
          )
        })}
        {!events.length && (
          <div className="match-detail-empty">{tr('No live events yet. Shot, corner and card deltas will appear here.')}</div>
        )}
      </div>
    </section>
  )
}

const TeamStructure = ({ match }) => {
  const { home, away, homeName, awayName } = getParticipants(match)
  const lineups = Array.isArray(match?.lineups) ? match.lineups : []
  const homeLineup = lineups.filter((item) => String(item.team_id || item.participant_id) === String(home?.id))
  const awayLineup = lineups.filter((item) => String(item.team_id || item.participant_id) === String(away?.id))
  const formation = match?.formations || {}

  const renderList = (list, typeId) =>
    list
      .filter((item) => Number(item.type_id) === typeId || (!item.type_id && typeId === 11))
      .sort((left, right) => (Number(left.jersey_number) || 99) - (Number(right.jersey_number) || 99))
      .map((item, index) => (
        <div className="team-structure-player" key={item.id || `${item.player_name}-${index}`}>
          <span>{item.jersey_number || '--'}</span>
          <strong>{item.player?.display_name || item.player?.common_name || item.player?.name || item.player_name || tr('Unknown player')}</strong>
        </div>
      ))

  const renderTeam = (name, list, side) => (
    <div className="team-structure-team">
      <div className="team-structure-title">
        <strong>{name}</strong>
        <span>{side === 'home' ? formation.localteam_formation : formation.visitorteam_formation || '--'}</span>
      </div>
      <div className="team-structure-section">{tr('Starting XI')}</div>
      {renderList(list, 11)}
      <div className="team-structure-section muted">{tr('Bench')}</div>
      {renderList(list, 12)}
      {!list.length && <div className="match-detail-empty">{tr('Lineups waiting for provider feed.')}</div>}
    </div>
  )

  return (
    <section className="match-detail-panel team-structure-panel">
      <div className="match-detail-panel-header compact">
        <span className="live-panel-label">{tr('Team Structure')}</span>
      </div>
      <div className="team-structure-grid">
        {renderTeam(homeName, homeLineup, 'home')}
        {renderTeam(awayName, awayLineup, 'away')}
      </div>
    </section>
  )
}

const formatStatValue = (label, value) => {
  const numericValue = Number(value) || 0
  if (label.includes('%') || label.includes('Percentage')) return `${Math.round(numericValue)}%`
  if (Math.abs(numericValue % 1) > 0) return numericValue.toFixed(2)
  return String(Math.round(numericValue))
}

const RadarStatsPanel = ({ match, stats }) => {
  const { homeName, awayName } = getParticipants(match)
  const shortName = (name) => String(name || '--').split(/\s+/).slice(0, 2).join(' ')

  return (
    <aside className="match-detail-panel radar-stats-panel">
      <div className="match-detail-panel-header compact radar-stats-title">
        <span className="live-panel-label">{tr('Live Radar Statistics', 'Estatísticas Radar Live')}</span>
        <strong>14 {tr('key stats', 'estatísticas-chave')}</strong>
      </div>
      <div className="radar-stats-head">
        <span>{tr('Stat')}</span>
        <strong>{shortName(homeName)}</strong>
        <strong>{shortName(awayName)}</strong>
      </div>
      <div className="radar-stats-list">
        {RADAR_STAT_DEFS.map(({ label, kind }) => {
          const home = Number(stats[label]?.home) || 0
          const away = Number(stats[label]?.away) || 0
          const total = home + away
          const homePct = total > 0 ? (home / total) * 100 : 50
          const awayPct = total > 0 ? (away / total) * 100 : 50

          return (
            <div className="radar-stat-row" key={label}>
              <span className={`radar-stat-icon event-${kind}`} aria-hidden="true"><TapeEventIcon kind={kind} /></span>
              <span className="radar-stat-label">{tr(label)}</span>
              <strong className="home">{formatStatValue(label, home)}</strong>
              <div className="radar-stat-balance" aria-hidden="true">
                <i className="home" style={{ width: `${homePct}%` }} />
                <i className="away" style={{ width: `${awayPct}%` }} />
              </div>
              <strong className="away">{formatStatValue(label, away)}</strong>
            </div>
          )
        })}
      </div>
    </aside>
  )
}

const MatchOrderBook = ({ match, stats }) => {
  const { homeName, awayName } = getParticipants(match)
  const shortName = (name) => String(name || '--').split(/\s+/).slice(0, 2).join(' ')

  return (
    <aside className="match-detail-panel match-order-book">
      <div className="match-detail-panel-header compact">
        <span className="live-panel-label">{tr('Match Order Book')}</span>
        <div className="order-book-toggle">
          <button type="button" className="active">%</button>
          <button type="button">{tr('Raw')}</button>
        </div>
      </div>
      <div className="order-book-head">
        <span>{tr('Stat')}</span>
        <strong>{shortName(homeName)}</strong>
        <span />
        <strong>{shortName(awayName)}</strong>
        <strong>{tr('Diff')}</strong>
      </div>
      {CATEGORY_DEFS.map((category) => (
        <div className="order-book-category" key={category.id}>
          <div className="order-book-category-title">{tr(category.label)}</div>
          {category.stats.map((label) => {
            const home = Number(stats[label]?.home) || 0
            const away = Number(stats[label]?.away) || 0
            const diff = home - away
            const total = home + away
            const homePct = total > 0 ? (home / total) * 100 : 50
            const awayPct = total > 0 ? (away / total) * 100 : 50
            return (
              <div className="order-book-row" key={label}>
                <span>{tr(label)}</span>
                <strong>{formatStatValue(label, home)}</strong>
                <div className="order-book-bars">
                  <i className="home" style={{ width: `${homePct}%` }} />
                  <i className="away" style={{ width: `${awayPct}%` }} />
                </div>
                <strong>{formatStatValue(label, away)}</strong>
                <strong className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral'}>{diff > 0 ? '+' : ''}{formatStatValue(label, diff)}</strong>
              </div>
            )
          })}
        </div>
      ))}
      <div className="order-book-mobile-list">
        {CATEGORY_DEFS.map((category) => (
          <details className="order-book-mobile-category" key={`mobile-${category.id}`} open={category.id === 'offensive'}>
            <summary className="order-book-mobile-summary">
              <span>{tr(category.label)}</span>
              <strong>{category.stats.length} {tr('Stats', 'stats')}</strong>
            </summary>
            <div className="order-book-mobile-panel">
              {category.stats.map((label) => {
                const home = Number(stats[label]?.home) || 0
                const away = Number(stats[label]?.away) || 0
                const diff = home - away
                const total = home + away
                const homePct = total > 0 ? (home / total) * 100 : 50
                const awayPct = total > 0 ? (away / total) * 100 : 50

                return (
                  <article className="order-book-mobile-stat" key={`mobile-${label}`}>
                    <div className="order-book-mobile-stat-head">
                      <span>{tr(label)}</span>
                      <strong className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral'}>
                        {diff > 0 ? '+' : ''}{formatStatValue(label, diff)}
                      </strong>
                    </div>
                    <div className="order-book-mobile-score">
                      <div>
                        <small>{shortName(homeName)}</small>
                        <strong>{formatStatValue(label, home)}</strong>
                      </div>
                      <div>
                        <small>{shortName(awayName)}</small>
                        <strong>{formatStatValue(label, away)}</strong>
                      </div>
                    </div>
                    <div className="order-book-bars order-book-bars-mobile">
                      <i className="home" style={{ width: `${homePct}%` }} />
                      <i className="away" style={{ width: `${awayPct}%` }} />
                    </div>
                  </article>
                )
              })}
            </div>
          </details>
        ))}
      </div>
    </aside>
  )
}

const OracleAnalyst = () => (
  <section className="match-detail-panel oracle-analyst-panel">
    <div className="match-detail-panel-header compact">
      <span className="live-panel-label">{tr('Oracle Analyst')} <small>beta</small></span>
    </div>
    <div className="oracle-chat-wrap">
      <OracleComingSoon />
    </div>
  </section>
)

const DetailSideTabs = ({ match, tapeEvents }) => {
  const [activeTab, setActiveTab] = useState('eventTape')
  const tabs = [
    { id: 'eventTape', label: tr('Event Tape') },
    { id: 'teamStructure', label: tr('Team Structure') },
    { id: 'oracleAnalyst', label: tr('Oracle Analyst') },
  ]

  return (
    <aside className="match-detail-tabs-panel">
      <div className="match-detail-tabs">
        {tabs.map((tab) => (
          <button
            className={activeTab === tab.id ? 'active' : ''}
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="match-detail-tab-content">
        {activeTab === 'eventTape' && <EventTape events={tapeEvents} />}
        {activeTab === 'teamStructure' && <TeamStructure match={match} />}
        {activeTab === 'oracleAnalyst' && <OracleAnalyst match={match} />}
      </div>
    </aside>
  )
}

const RADAR_TIMELINE_EVENT_KINDS = new Set(['goal', 'yellow-card', 'shot-target', 'save', 'corner'])

const RadarTimeline = ({ match, events, professionalIcons = false }) => {
  const { homeName, awayName } = getParticipants(match)
  const currentMinute = getCurrentMinuteNumber(match)
  const endMinute = Math.max(90, getChartEndMinute(match, []))
  const ticks = getChartTicks(endMinute)
  const visibleEvents = [...(events || [])]
    .filter((event) =>
      Number(event?.minute) > 0 && RADAR_TIMELINE_EVENT_KINDS.has(getTapeEventVisual(event).kind)
    )
    .sort((left, right) => Number(left.minute) - Number(right.minute))

  return (
    <section className="radar-timeline-panel" aria-label={tr('Match event timeline', 'Timeline de eventos da partida')}>
      <div className="radar-timeline-head">
        <span><i className="radar-live-dot" /> {tr('Live event timeline', 'Timeline de eventos live')}</span>
        <strong>{currentMinute || 0}' / {endMinute}'</strong>
      </div>
      <div className="radar-timeline-team-legend">
        <span className="home"><i /> {homeName}<small>ACIMA</small></span>
        <span className="away"><small>ABAIXO</small>{awayName} <i /></span>
      </div>
      <div className="radar-timeline-track">
        <div className="radar-timeline-progress" style={{ width: `${clamp((currentMinute / endMinute) * 100)}%` }} />
        {ticks.map((tick) => (
          <span className="radar-timeline-tick" key={tick} style={{ left: `${(tick / endMinute) * 100}%` }}>
            <i />
            <small>{tick}</small>
          </span>
        ))}
        {visibleEvents.map((event, index) => {
          const eventCopy = getTapeEventCopy(event)
          const eventVisual = getTapeEventVisual(event)
          const sequence = Number.isFinite(Number(event.sequence)) ? Number(event.sequence) : index
          const markerMinute = Math.min(endMinute, Number(event.minute) + (sequence % 5) * 0.12)
          return (
            <span
              className={`radar-timeline-event side-${event.side || 'home'} tone-${event.tone || 'neutral'} event-${eventVisual.kind}`}
              key={`${event.id || event.title}-${index}`}
              style={{ left: `${clamp((markerMinute / endMinute) * 100)}%` }}
              title={`${event.minute}' · ${eventCopy.title} · ${event.teamName || ''}`}
            >
              <span aria-hidden="true">
                {professionalIcons
                  ? <TapeEventIcon kind={eventVisual.kind} />
                  : getLegacyTapeEventSymbol(event, eventVisual)}
              </span>
            </span>
          )
        })}
      </div>
    </section>
  )
}

const XgAccumulationView = ({ history, match }) => {
  const { homeName, awayName } = getParticipants(match)
  const data = (Array.isArray(history) ? history : [])
    .map((point) => ({
      minute: getClassicPreciseMinute(point),
      homeXg: Number(point?.home?.xg ?? point?.stats?.home?.xg) || 0,
      awayXg: Number(point?.away?.xg ?? point?.stats?.away?.xg) || 0,
    }))
    .filter((point) => Number.isFinite(point.minute))
    .sort((left, right) => left.minute - right.minute)
  const latest = data[data.length - 1] || { homeXg: 0, awayXg: 0 }
  const endMinute = Math.max(90, getChartEndMinute(match, data))
  const goalEvents = buildMarketGoalEvents(match)

  return (
    <section className="xg-accumulation-view">
      <div className="xg-summary-grid">
        <article className="xg-summary-card home">
          <span>{homeName}</span>
          <strong>{latest.homeXg.toFixed(2)} xG</strong>
        </article>
        <article className="xg-summary-card away">
          <span>{awayName}</span>
          <strong>{latest.awayXg.toFixed(2)} xG</strong>
        </article>
      </div>
      <div className="xg-chart-panel">
        <div className="match-detail-panel-header compact">
          <span className="live-panel-label">{tr('Accumulated xG', 'xG acumulado')}</span>
          <small>{tr('FilterMarket live estimate', 'Estimativa live FilterMarket')}</small>
        </div>
        {data.length ? (
          <ResponsiveContainer width="100%" height={430}>
            <ComposedChart data={data} margin={{ top: 24, right: 24, left: 0, bottom: 12 }}>
              <CartesianGrid stroke="#1b2529" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="minute"
                domain={[0, endMinute]}
                type="number"
                ticks={getChartTicks(endMinute)}
                tick={{ fill: '#738078', fontSize: 10 }}
                tickFormatter={(value) => `${Math.round(value)}'`}
              />
              <YAxis tick={{ fill: '#738078', fontSize: 10 }} width={34} />
              <Tooltip content={<FlowTooltip />} />
              {goalEvents.map((event) => (
                <ReferenceLine key={event.id} x={event.minute} stroke="#ffffff" strokeDasharray="2 4" strokeOpacity={0.35} />
              ))}
              <Area type="stepAfter" dataKey="homeXg" fill="#19f05a" fillOpacity={0.1} stroke="none" />
              <Area type="stepAfter" dataKey="awayXg" fill="#ff4b3e" fillOpacity={0.08} stroke="none" />
              <Line type="stepAfter" dataKey="homeXg" name={homeName} stroke="#19f05a" strokeWidth={2.6} dot={false} />
              <Line type="stepAfter" dataKey="awayXg" name={awayName} stroke="#ff4b3e" strokeWidth={2.6} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="match-detail-empty">{tr('Collecting xG snapshots.', 'A recolher snapshots de xG.')}</div>
        )}
      </div>
    </section>
  )
}

const TradingRadarView = ({ match, history, stats, tapeEvents, radarMode = false }) => (
  <section className="trading-radar-view">
    <RadarTimeline match={match} events={tapeEvents} professionalIcons={radarMode} />
    <div className="trading-radar-grid">
      <main className="trading-radar-main">
        <ClassicPressureChart history={history} match={match} />
        <EventTape events={tapeEvents} professionalIcons={radarMode} />
      </main>
      {radarMode ? <RadarStatsPanel match={match} stats={stats} /> : <MatchOrderBook match={match} stats={stats} />}
    </div>
  </section>
)

const LiveEventsFocusView = ({ match, tapeEvents, radarMode = false }) => (
  <section className="events-focus-view">
    <RadarTimeline match={match} events={tapeEvents} professionalIcons={radarMode} />
    <EventTape events={tapeEvents} professionalIcons={radarMode} />
  </section>
)

const MatchHeader = ({ match, onBack, radarMode = false }) => {
  const { homeName, awayName } = getParticipants(match)
  const score = getScore(match)
  const risk = getRisk(match)

  return (
    <header className="match-detail-header">
      <button type="button" className="match-back-button" onClick={onBack}>
        <i className={`bi ${radarMode ? 'bi-x-lg' : 'bi-arrow-left'}`} />
        {radarMode ? tr('Close radar', 'Fechar radar') : tr('Overview')}
      </button>
      <div className="match-scoreboard">
        <div className="match-time-box">
          <strong>{getMinute(match)}</strong>
          <span>{tr(String(match?.currentPeriod?.description || match?.current_period?.description || 'Live'))}</span>
        </div>
        <div className="match-team-name home">{homeName}</div>
        <div className="match-score-box">{score.home} - {score.away}</div>
        <div className="match-team-name away">{awayName}</div>
      </div>
      <div className="match-meta-strip">
        <div><span>{tr('League')}</span><strong>{getLeagueLabel(match)}</strong></div>
        <div><span>{tr('Venue')}</span><strong>{match?.venue?.name || '--'}</strong></div>
        {!radarMode && <div><span>{tr('Last update')}</span><strong>{formatLastUpdate(match?.lastUpdate)}</strong></div>}
        <div><span>{tr('Risk', 'Risco')}</span><strong className={`risk-${risk.tone}`}>{risk.label}</strong></div>
        {!radarMode && <div><span>{tr('Trigger')}</span><strong>{tr(getTriggerLabel(match))}</strong></div>}
      </div>
      {!radarMode && (
        <div className="match-state-card">
          <span>{tr('Market State')}</span>
          <strong>{tr(match?.marketState?.label || 'Unclear')}</strong>
          <small>{tr(match?.marketState?.reason || 'Live pattern still forming.')}</small>
        </div>
      )}
    </header>
  )
}

const LiveMatchDetailPage = ({ match, onBack, radarMode = false }) => {
  const [activeView, setActiveView] = useState(() => radarMode ? 'radar' : 'flow')
  const stats = useMemo(() => collectOrderBookStats(match), [match])
  const flowHistory = useMarketFlowHistory(match)
  const classicHistory = useClassicHistory(match, stats)
  const syntheticEvents = useSyntheticEventTape(match, stats, radarMode)
  const tapeEvents = useMemo(
    () => buildTapeEvents(match, syntheticEvents, radarMode),
    [match, radarMode, syntheticEvents]
  )

  useEffect(() => {
    if (!radarMode || !match) return undefined
    const previousTitle = document.title
    const { homeName, awayName } = getParticipants(match)
    const score = getScore(match)
    document.title = `${homeName} ${score.home}-${score.away} ${awayName} | FilterMarket Radar`
    return () => {
      document.title = previousTitle
    }
  }, [match, radarMode])

  if (!match) {
    return (
      <div className="match-detail-shell">
        <div className="match-detail-empty">{tr('Match not found in the current live feed.')}</div>
      </div>
    )
  }

  return (
    <div className={`match-detail-shell ${radarMode ? 'radar-mode' : ''} ${activeView === 'events' ? 'events-focus-active' : ''}`}>
      <MatchHeader match={match} onBack={onBack} radarMode={radarMode} />
      <div className="match-view-tabs">
        <span className="live-panel-label">{radarMode ? tr('Trading Radar') : tr('Market Flow')}</span>
        <button
          className={activeView === 'radar' ? 'active' : ''}
          onClick={() => setActiveView('radar')}
          type="button"
        >
          {tr('Live Radar', 'Radar Live')}
        </button>
        <button
          className={activeView === 'flow' ? 'active' : ''}
          onClick={() => setActiveView('flow')}
          type="button"
        >
          {tr('Indexes / Flow', 'Índices / Flow')}
        </button>
        <button
          className={activeView === 'classic' ? 'active' : ''}
          onClick={() => setActiveView('classic')}
          type="button"
        >
          {tr('Classic')}
        </button>
        <button
          className={activeView === 'events' ? 'active' : ''}
          onClick={() => setActiveView('events')}
          type="button"
        >
          {tr('Live Events', 'Eventos Live')}
        </button>
        <button
          className={activeView === 'xg' ? 'active' : ''}
          onClick={() => setActiveView('xg')}
          type="button"
        >
          {tr('Accumulated xG', 'xG acumulado')}
        </button>
        {radarMode && (
          <button
            className={activeView === 'orderbook' ? 'active' : ''}
            onClick={() => setActiveView('orderbook')}
            type="button"
          >
            {tr('Orderbook')}
          </button>
        )}
      </div>
      <div className={`match-detail-layout ${activeView !== 'flow' ? 'match-detail-layout-classic' : ''}`}>
        {activeView === 'radar' ? (
          <TradingRadarView match={match} history={classicHistory} stats={stats} tapeEvents={tapeEvents} radarMode={radarMode} />
        ) : activeView === 'classic' ? (
          <main className="match-detail-main classic-detail-main">
            <ClassicView history={classicHistory} match={match} stats={stats} />
          </main>
        ) : activeView === 'events' ? (
          <main className="match-detail-main events-focus-main">
            <LiveEventsFocusView match={match} tapeEvents={tapeEvents} radarMode={radarMode} />
          </main>
        ) : activeView === 'xg' ? (
          <main className="match-detail-main classic-detail-main">
            <XgAccumulationView history={classicHistory} match={match} />
          </main>
        ) : activeView === 'orderbook' && radarMode ? (
          <main className="match-detail-main poly-orderbook-main">
            <PolymarketOrderBook match={match} />
          </main>
        ) : (
          <>
            <main className="match-detail-main">
              <div className={`match-market-workspace ${radarMode ? 'radar-flow-workspace' : ''}`}>
                <MarketFlow history={flowHistory} match={match} radarMode={radarMode} />
                {!radarMode && <DetailSideTabs match={match} tapeEvents={tapeEvents} />}
              </div>
            </main>
            <MatchOrderBook match={match} stats={stats} />
          </>
        )}
      </div>
    </div>
  )
}

export default LiveMatchDetailPage
